"use client"

import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { usePathname, useSearchParams } from "next/navigation"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { SearchParams } from "@ub/fact-finder-client"
import { useTranslations } from "next-intl"

import { CategorySearchResult, FacetData } from "@/lib/factfinder/factfinder-interfaces"
import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { resolveStoryblokStoryHref } from "@/lib/link-utils/storyblok-link-resolver"
import { DEFAULT_MARKET_CHANNEL, type MarketChannel } from "@/lib/market/market-channel"
import { ApplicationSettings } from "@/lib/settings/application-settings"
import { Category } from "@/lib/storefront/category/category"
import { CategoryContext } from "@/lib/storefront/category/category-service"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { Button } from "@/components/ui/components"
import FallbackCard from "@/components/common/FallbackCard"
import { GuidedSellingContainer } from "@/components/guided-selling/guided-selling-container"
import { CategoryBrand } from "@/components/search-and-category/CategoryBrandsSection"
import CategoryBrandsSectionTrackingClient from "@/components/search-and-category/CategoryBrandsTrackingClient"
import CustomFacetList from "@/components/search-and-category/customfilter/CustomFacetList"
import GoToAccessoryCategoryButton from "@/components/search-and-category/customfilter/GoToAccessoryCategoryButton"
import FacetList from "@/components/search-and-category/FacetList"
import { FilterButtonClient } from "@/components/search-and-category/FilterButtonClient"
import SearchResultArticleView from "@/components/search-and-category/SearchResultArticleView"
import SearchResultContainer from "@/components/search-and-category/SearchResultContainer"
import SearchResultContainerGridList from "@/components/search-and-category/SearchResultContainerGridList"
import {
  CategoryContentStoryblok,
  UbCategoryResultsStoryblok,
  UbGlobalGuidedSellingStoryblok,
} from "@/components/storyblok/component-types"
import { resolveListingApiUrl } from "@/components/storyblok/listing-api-client"

interface CategoryListingResponse {
  categoryContext?: CategoryContext
  categoryBrands?: CategoryBrand[]
  top10Hits?: ReducedProductData[]
  ffSearchParams?: SearchParams
  isFiltered: boolean
  category?: Category
  error?: string
}

interface UBCategoryResultsClientProps {
  blok: UbCategoryResultsStoryblok
  categoryUrlSlug: string
  category: Category
  market: string
  channel?: MarketChannel
  sbLocale: string
  isoLocale: string
  defaultFacetsToHide: string[]
  applicationSettings: ApplicationSettings
  categoryContent?: CategoryContentStoryblok
  guidedSellingSettings?: UbGlobalGuidedSellingStoryblok
  guidedSellingSummaryTop?: React.ReactNode
  guidedSellingSummaryBottom?: React.ReactNode
  hideProductListing?: boolean
  guidedSellingPortalTargetId?: string
  guidedSellingClassName?: string
  subcategoriesContainerId?: string
  hideSubcategoriesOnGuidedSellingResult?: boolean
}

function getBrandIndexLink(
  blok: UbCategoryResultsStoryblok,
  applicationSettings: ApplicationSettings,
  sbLocale: string
) {
  if (blok.brandIndexLink?.linktype === "story" && blok.brandIndexLink.cached_url) {
    return resolveStoryblokStoryHref(blok.brandIndexLink.cached_url, sbLocale, blok.brandIndexLink.story)
  }

  if (blok.brandIndexLink?.linktype === "url" && blok.brandIndexLink.url) {
    return getLinkWithPrefixFromURLAndLocale(blok.brandIndexLink.url, sbLocale)
  }

  return getLinkWithPrefixFromURLAndLocale(applicationSettings.brandIndexLink || "/brands", sbLocale)
}

export default function UBCategoryResultsClient({
  blok,
  categoryUrlSlug,
  category,
  market,
  channel = DEFAULT_MARKET_CHANNEL,
  sbLocale,
  isoLocale,
  defaultFacetsToHide,
  applicationSettings,
  categoryContent,
  guidedSellingSettings,
  guidedSellingSummaryTop,
  guidedSellingSummaryBottom,
  hideProductListing = false,
  guidedSellingPortalTargetId,
  guidedSellingClassName,
  subcategoriesContainerId,
  hideSubcategoriesOnGuidedSellingResult = false,
}: UBCategoryResultsClientProps) {
  const t = useTranslations("storefront")
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { setIsLoading } = useUbStorefrontState()
  const [data, setData] = useState<CategoryListingResponse | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [guidedSellingPortalTarget, setGuidedSellingPortalTarget] = useState<HTMLElement | null>(null)
  const [guidedSellingClosed, setGuidedSellingClosed] = useState(true)
  const [guidedSellingCompleted, setGuidedSellingCompleted] = useState(false)

  const queryString = searchParams.toString()
  const templateBlok = blok.resultDisplayType[0]
  const allowDebugMode = applicationSettings.allowDebugMode ?? false
  const hideAddToBasket = applicationSettings.hideAddToBasketButton ?? false
  const customFilterEnabled = !!applicationSettings.customFilterEnabled && !!categoryContent?.filterActive
  const brandIndexLink = getBrandIndexLink(blok, applicationSettings, sbLocale)

  useEffect(() => {
    const controller = new AbortController()
    const apiSearchParams = new URLSearchParams(queryString)
    apiSearchParams.set("market", market)
    apiSearchParams.set("channel", channel)
    apiSearchParams.set("categorySlug", categoryUrlSlug)
    if (categoryContent?.top10UntilFilter) {
      apiSearchParams.set("includeTop10", "true")
    }

    setIsLoading(true)
    setError(undefined)

    resolveListingApiUrl("category", apiSearchParams, { sbLocale, channel, signal: controller.signal })
      .then((url) =>
        fetch(url, {
          signal: controller.signal,
        })
      )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(await response.text())
        }
        return response.json() as Promise<CategoryListingResponse>
      })
      .then(setData)
      .catch((e) => {
        if (!controller.signal.aborted) {
          setError(e instanceof Error ? e.message : "Unable to load category results")
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      })

    return () => controller.abort()
  }, [categoryContent?.top10UntilFilter, categoryUrlSlug, channel, market, queryString, sbLocale, setIsLoading])

  useEffect(() => {
    if (!guidedSellingPortalTargetId) {
      setGuidedSellingPortalTarget(null)
      return
    }

    setGuidedSellingPortalTarget(document.getElementById(guidedSellingPortalTargetId))
  }, [guidedSellingPortalTargetId])

  const showGuidedSellingResult = guidedSellingCompleted && !guidedSellingClosed

  useEffect(() => {
    if (!subcategoriesContainerId || !hideSubcategoriesOnGuidedSellingResult) {
      return
    }

    const subcategoriesContainer = document.getElementById(subcategoriesContainerId)
    subcategoriesContainer?.classList.toggle("hidden", showGuidedSellingResult)
  }, [hideSubcategoriesOnGuidedSellingResult, showGuidedSellingResult, subcategoriesContainerId])

  if (error) {
    if (hideProductListing) {
      return null
    }

    return <div className="rounded-sm border border-red-200 bg-red-50 p-4 text-sm">{t("category.products.empty")}</div>
  }

  if (!data) {
    if (hideProductListing) {
      return null
    }

    return <FallbackCard className="min-h-480 bg-gray-100" />
  }

  const searchResult: CategorySearchResult | undefined = data.categoryContext?.searchResult
  const facets: FacetData[] = searchResult?.facets ?? []
  const allFacets: FacetData[] = data.categoryContext?.allFacets ?? []
  const hitcount = searchResult?.hitcount ?? 0
  const showCustomFilterSpace = customFilterEnabled && !!categoryContent?.customFilterSpace?.[0]
  const showTop10 = !!categoryContent?.top10UntilFilter && !data.isFiltered && !!data.top10Hits?.length
  const guidedSellingCampaign = categoryContent?.showGuidedSellingBlock
    ? (searchResult?.campaigns?.find((campaign) => campaign.flavour === "ADVISOR") ?? undefined)
    : undefined
  const effectiveHideProductListing = hideProductListing && !showGuidedSellingResult
  const guidedSellingElement =
    guidedSellingCampaign &&
    guidedSellingSettings?.show_guided_selling_block &&
    guidedSellingSettings.label_breadcrumb_start &&
    guidedSellingSettings.label_breadcrumb_end &&
    guidedSellingSettings.label_from_to &&
    guidedSellingSettings.label_or &&
    guidedSellingSettings.label_btn_confirm &&
    guidedSellingSettings.label_btn_reset &&
    guidedSellingSettings.label_btn_open ? (
      <GuidedSellingContainer
        className={guidedSellingClassName}
        campaign={guidedSellingCampaign}
        label_breadcrumb_start={guidedSellingSettings.label_breadcrumb_start}
        label_breadcrumb_end={guidedSellingSettings.label_breadcrumb_end}
        label_from_to={guidedSellingSettings.label_from_to}
        label_or={guidedSellingSettings.label_or}
        label_btn_confirm={guidedSellingSettings.label_btn_confirm}
        label_btn_reset={guidedSellingSettings.label_btn_reset}
        label_btn_open={guidedSellingSettings.label_btn_open}
        blok_summary_top={guidedSellingSummaryTop}
        blok_summary_bottom={guidedSellingSummaryBottom}
        trackingPageType="category"
        totalHits={hitcount}
        ffSearchParams={searchResult?.effectiveFFParams}
        showCloseButton={true}
        defaultClosed={true}
        onClosedChange={setGuidedSellingClosed}
        onSummaryPageChange={setGuidedSellingCompleted}
      />
    ) : null

  return (
    <>
      {guidedSellingPortalTarget && guidedSellingElement && createPortal(guidedSellingElement, guidedSellingPortalTarget)}
      {!effectiveHideProductListing && (
        <div className="grid w-full grid-cols-1 flex-col pt-5 lg:grid-cols-[auto_minmax(min-content,16rem)_minmax(auto,84rem)_auto]">
          <div className="lg:col-start-2 lg:row-start-1 lg:pl-5">
            <FacetList
              facetManager={categoryContent?.facetManager}
              sbLocale={sbLocale}
              isoLocale={isoLocale}
              defaultFacetsToHide={defaultFacetsToHide}
              facets={facets}
              baseCategory={category}
              categoryAsTree={true}
              hitcount={hitcount}
              isFiltered={data.isFiltered}
              categoryContent={categoryContent}
              customFilterEnabled={customFilterEnabled}
              useFacetConfiguration={applicationSettings.useFacetConfiguration ?? false}
              ffSearchParams={data.ffSearchParams}
            />
          </div>

          <div className="row-start-2 flex w-full flex-col gap-y-5 px-5 lg:col-start-3 lg:row-start-1">
            {showCustomFilterSpace && (
              <div className="flex flex-col gap-2">
                <CustomFacetList
                  sbLocale={sbLocale}
                  isoLocale={isoLocale}
                  facets={facets}
                  allFacets={allFacets}
                  baseCategory={category}
                  categoryOnly={category.path.length === 0}
                  categoryAsTree={true}
                  hitcount={hitcount}
                  showAboveFilterButton={true}
                  isFiltered={data.isFiltered}
                  categoryContent={categoryContent}
                  customFilterEnabled={customFilterEnabled}
                  ffSearchParams={data.ffSearchParams}
                />
                <div className="flex flex-row flex-wrap justify-between gap-2">
                  <div className="flex flex-row flex-wrap justify-start gap-2">
                    {categoryContent?.showAccessoriesButton &&
                      categoryContent?.accessoriesButtonLink?.cached_url &&
                      categoryContent?.accessoriesButtonLink?.cached_url !== "" && (
                        <GoToAccessoryCategoryButton categoryContent={categoryContent} />
                      )}
                    {categoryContent?.showProductInfoButton && (
                      <a href="#categoryContent">
                        <Button variant="outline_secondary" size="defaultNoGrow">
                          {categoryContent?.productInfoButtonText ?? "Produktinformationen"}
                        </Button>
                      </a>
                    )}
                  </div>
                  {data.isFiltered && (
                    <a className="underline hover:text-primary" href={pathname}>
                      {categoryContent?.removeAllText ?? t("common.reset_filters")}
                    </a>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-row items-center justify-center bg-ub-background lg:hidden">
              <FilterButtonClient />
            </div>
            {!guidedSellingPortalTarget && guidedSellingElement}
            {showTop10 ? (
              <div className="flex flex-col gap-4">
                <div className="grid auto-rows-max grid-cols-[1fr_auto_1fr] gap-2">
                  <div />
                  <h3 className="flex items-center justify-center text-center text-xl font-semibold text-wrap hyphens-auto md:text-2xl">
                    {categoryContent?.labelTop10Header ?? t("category.topseller")}
                  </h3>
                  <div className="flex w-auto grow justify-end">
                    <SearchResultArticleView defaultView="list" />
                  </div>
                </div>
                <SearchResultContainerGridList
                  isB2B={true}
                  allowDebugMode={allowDebugMode}
                  hideAddToBasket={hideAddToBasket}
                  inStoryBlok={false}
                  tracking="category"
                  products={data.top10Hits ?? []}
                  sbLocale={sbLocale}
                  channel={channel}
                  isoLocale={isoLocale}
                  templateBlok={templateBlok}
                  banner={undefined}
                  bannerPosition={undefined}
                  bannerCampaignId={undefined}
                  categoryPath={[...category.path, category.name]}
                  searchTerm={`category_${category.name}`}
                  campaignId={guidedSellingCampaign?.id}
                />
              </div>
            ) : (
              <SearchResultContainer
                isB2B={true}
                allowDebugMode={allowDebugMode}
                hideAddToBasket={hideAddToBasket}
                inStoryblock={false}
                tracking="category"
                sbLocale={sbLocale}
                channel={channel}
                isoLocale={isoLocale}
                products={searchResult?.hits ?? []}
                templateBlok={templateBlok}
                productsTotal={hitcount}
                banner={searchResult?.banner}
                bannerPosition={blok.bannerPosition !== undefined ? Number(blok.bannerPosition) : undefined}
                bannerCampaignId={searchResult?.bannerCampaignId}
                categoryPath={[...category.path, category.name]}
                searchTerm={`category_${category.name}`}
                campaignId={guidedSellingCampaign?.id}
              />
            )}
          </div>
        </div>
      )}

      {!!data.categoryBrands?.length && (
        <CategoryBrandsClient
          categoryBrands={data.categoryBrands}
          category={category}
          brandIndexLink={brandIndexLink}
        />
      )}
    </>
  )
}

function CategoryBrandsClient(props: { categoryBrands: CategoryBrand[]; category: Category; brandIndexLink: string }) {
  const t = useTranslations("storefront")

  return (
    <div className="mt-8 flex w-full flex-col items-center justify-center gap-8 bg-black px-2 py-8 text-white">
      <CategoryBrandsSectionTrackingClient
        categoryBrands={props.categoryBrands}
        category={props.category}
        brandIndexLink={props.brandIndexLink}
      />
      <h2 className="mb-4 block text-center text-3xl font-bold text-white 3xl:text-5xl sm:text-4xl">
        {t("category.brands.headline", { brand: props.category.name })}
      </h2>
      <div
        style={{
          ["--columnsCountSm" as string]: `${Math.min(2, props.categoryBrands.length)}`,
          ["--columnsCountMd" as string]: `${Math.min(3, props.categoryBrands.length)}`,
          ["--columnsCountXl" as string]: `${Math.min(6, props.categoryBrands.length)}`,
        }}
        className="
          grid grid-cols-1 gap-4
          sm:grid-cols-[repeat(var(--columnsCountSm),1fr)]
          md:grid-cols-[repeat(var(--columnsCountMd),1fr)]
          xl:grid-cols-[repeat(var(--columnsCountXl),1fr)]
        "
      >
        {props.categoryBrands.map((categoryBrand) => (
          <a
            key={categoryBrand.name}
            className="flex h-21 w-44 items-center justify-center bg-white"
            href={categoryBrand.link}
          >
            <img className="h-16 w-32 object-contain" alt={categoryBrand.name} src={categoryBrand.img} />
          </a>
        ))}
      </div>
      <a href={props.brandIndexLink} target="_self" className="mx-auto my-4 block bg-cover lg:mx-auto">
        <Button size="lg">{t("common.show_all")}</Button>
      </a>
    </div>
  )
}
